import React from 'react';
import { MapPin, Shield, Target, Award } from 'lucide-react';
import SEO from '../components/SEO';

const About = () => {
  const values = [
    { icon: Target, title: 'Atsakomybė', description: 'Atsakingai rūpinamės kiekviena detale' },
    { icon: Award, title: 'Tikslumas', description: 'Tikslūs procesai ir ataskaitos' },
    { icon: Shield, title: 'Skaidrumas', description: 'Aiškūs įkainiai ir pilna kontrolė' },
    { icon: MapPin, title: 'Lankstumas', description: 'Prisitaikome prie jūsų poreikių' },
  ];

  return (
    <div className="min-h-screen" data-testid="about-page">
      <SEO 
        title="Apie WSK Logistiką | Modernus sandėlis Kaune, Nemajūnų g."
        description="WSK Logistika – patikimas logistikos partneris Kaune. 2000 m² modernus sandėlis, aukštas saugumo lygis ir individualūs sprendimai Jūsų verslui."
        keywords="WSK Logistika, sandėlis Kaune, logistikos centras, 3PL partneris, WMS sistema, prekių saugojimas"
      />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-600 to-blue-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Apie mus</h1>
            <p className="text-xl text-blue-100">
              Patikimas logistikos partneris, teikiantis sandėliavimo ir 3PL paslaugas verslui Kaune
            </p>
          </div>
        </div>
      </section>

      {/* Who We Are */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Kas mes esame</h2>
              <p className="text-lg text-gray-600 mb-4">
                MB „WSK Logistikos paslaugos" – patikimas logistikos partneris, teikiantis
                sandėliavimo ir 3PL paslaugas verslui Kaune.
              </p>
              <p className="text-lg text-gray-600 mb-4">
                Mūsų logistikos centras įsikūręs strateginėje vietoje – Kaune, Nemajūnų g. 19,
                netoli pagrindinių Lietuvos magistralių A1, A6 ir A5.
              </p>
              <p className="text-lg text-gray-600">
                Tai užtikrina greitą krovinių judėjimą ir patogų privažiavimą tiek iš Vilniaus,
                tiek iš uosto miestų bei kitų Lietuvos regionų.
              </p>
            </div>
            <div className="relative h-96 rounded-xl overflow-hidden shadow-xl">
              <img
                src="/images/photo-1748346918817-0b1b6b2f9bab.jpeg"
                alt="WSK Logistika team"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Infrastructure */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1 relative h-96 rounded-xl overflow-hidden shadow-xl">
              <img
                src="/images/pexels-photo-8961260.jpeg"
                alt="Warehouse facility"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="order-1 lg:order-2">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Infrastruktūra ir saugumas
              </h2>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <span className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm font-bold mr-3 mt-0.5">
                    ✓
                  </span>
                  <span className="text-lg text-gray-600">
                    2000 kv. m modernios sandėlio patalpos
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm font-bold mr-3 mt-0.5">
                    ✓
                  </span>
                  <span className="text-lg text-gray-600">
                    Aptverta ir saugoma teritorija
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm font-bold mr-3 mt-0.5">
                    ✓
                  </span>
                  <span className="text-lg text-gray-600">
                    Signalizacija ir tiesioginis ryšys su apsaugos įmone
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm font-bold mr-3 mt-0.5">
                    ✓
                  </span>
                  <span className="text-lg text-gray-600">
                    Nuolatinis prekių draudimas
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm font-bold mr-3 mt-0.5">
                    ✓
                  </span>
                  <span className="text-lg text-gray-600">
                    Šiuolaikinė sandėlio valdymo sistema (WMS)
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-16 bg-blue-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-6">Mūsų misija</h2>
          <p className="text-xl text-blue-100">
            Išlaisvinti klientų laiką ir energiją nuo logistikos rūpesčių, kad jie galėtų
            susitelkti į savo pagrindinės veiklos plėtrą ir verslo augimą.
          </p>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Mūsų vertybės</h2>
            <p className="text-xl text-gray-600">
              Principai, kuriais vadovaujamės kiekvienoje savo darbo dienoje
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div
                  key={index}
                  className="bg-gray-50 rounded-xl p-6 text-center hover:shadow-lg transition-shadow"
                >
                  <div className="inline-flex items-center justify-center bg-blue-600 p-4 rounded-full mb-4">
                    <Icon className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{value.title}</h3>
                  <p className="text-gray-600">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
